import { apiRequest } from "@/api/ApiRequest";
import { ApiMethod } from "@/types/Enums";

import type {
  MessageItem,
  MessageSearch,
  MessageSend,
  MessageDelete,
  MessageReSendInfo,
  MessageDetail,
  CourseOption,
  CourseOptionSearch,
  UserTarget,
  UserTargetSearch,
  AttendanceSearch,
  SmsSendInfo,
  SmsHistoryReq,
  SmsHistory,
} from "@/types/lms/MessageType";
import type { CudResultType } from "@/types/cmmn/CmmnTypes";

// 보낸 쪽지 / 받는 쪽지 목록 조회
export const getMessageList = async (params: MessageSearch): Promise<MessageItem[]> => {
  return await apiRequest<MessageItem[]>({
    url: "/api/v1/auth/message",
    method: ApiMethod.GET,
    params: params as unknown as Record<string, unknown>,
    useLoading: true,
    noAlert: true, // 컴포넌트 자체 완료 dialog 사용 → 자동 alert 중복 방지
  });
};

// 보낸 쪽지 / 받는 쪽지 상세 조회
// type: "sent" 일 때만 BE 가 receiverList(전체 수신자) 를 조회한다 — inbox 는 화면에 안 쓰여 생략(응답 지연 단축)
export const getMessage = async (msgSendHisId: string, type?: string): Promise<MessageDetail> => {
  return await apiRequest<MessageDetail>({
    url: `/api/v1/auth/message/${msgSendHisId}`,
    method: ApiMethod.GET,
    params: type ? { type } : undefined,
  });
};

// 쪽지 보내기 (multipart: param(JSON) + files)
export const sendMessage = async (
  data: MessageSend,
  files: File[] = []
): Promise<CudResultType> => {
  const form = new FormData();
  form.append("param", new Blob([JSON.stringify(data)], { type: "application/json" }));
  files.forEach((f) => form.append("files", f));

  return await apiRequest({
    url: "/api/v1/auth/message",
    method: ApiMethod.POST,
    noAlert: true,
    data: form as unknown as Record<string, unknown>,
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// 쪽지 재전송 (실패 건만)
export const resendMessage = async (data: MessageReSendInfo): Promise<CudResultType> => {
  return await apiRequest({
    url: `/api/v1/auth/message/resend`,
    method: ApiMethod.POST,
    data: data as unknown as Record<string, unknown>,
  });
};

// 쪽지 수신자 선택용 과정 옵션 목록 조회
export const getCourseOptions = async (params: CourseOptionSearch): Promise<CourseOption[]> => {
  return await apiRequest<CourseOption[]>({
    url: "/api/v1/cmmn/courses",
    method: ApiMethod.GET,
    params: params as unknown as Record<string, unknown>,
  });
};

// 쪽지 수신자 대상 목록 조회 (역할별 자동 분기)
export const getMessageTargets = async (params: UserTargetSearch = {}): Promise<UserTarget[]> => {
  return await apiRequest<UserTarget[]>({
    url: "/api/v1/cmmn/message-targets",
    method: ApiMethod.GET,
    params: params as unknown as Record<string, unknown>,
  });
};

// 출결 상태별 userId 목록 조회 (지각/결석 필터)
export const getAttendanceUsers = async (params: AttendanceSearch): Promise<string[]> => {
  return await apiRequest<string[]>({
    url: "/api/v1/cmmn/attendance-users",
    method: ApiMethod.GET,
    params: params as unknown as Record<string, unknown>,
  });
};

// 쪽지 삭제 (받는 쪽지함에서는 수신자 기준, 보낸 쪽지함에서는 발신자 기준으로 삭제)
// SOFT DELETE 처리 (delYn = 'Y'로 업데이트) - 실제 데이터는 삭제되지 않음
export const deleteMessage = async (data: MessageDelete): Promise<CudResultType> => {
  return await apiRequest({
    url: "/api/v1/auth/message",
    method: ApiMethod.PUT,
    noAlert: true,
    data: data as unknown as Record<string, unknown>,
  });
};

// SMS 보내기
export const sendSms = async (data: SmsSendInfo): Promise<CudResultType> => {
  return await apiRequest({
    url: "/api/v1/auth/message/sms",
    method: ApiMethod.POST,
    data: data as unknown as Record<string, unknown>,
    noAlert: true,
  });
};

// SMS 발송 내역 조회
export const getSmsHistory = async (params: SmsHistoryReq): Promise<SmsHistory[]> => {
  return await apiRequest<SmsHistory[]>({
    url: "/api/v1/auth/message/sms/history",
    method: ApiMethod.GET,
    params: params as unknown as Record<string, unknown>,
    noAlert: true,
  });
};
